import { idOf } from '@hds/auto-auction-com-client-sdk'

/**
 * The Live Sales page's one derivation: which of a house's sales are running,
 * and which lanes each one runs.
 *
 * The runtime does not know which house an event belongs to — its event
 * record carries a name, a lane, a city, a type and a run list, and nothing
 * else. Two things do know. vibe-server's sale records are one lane each, and
 * each carries the sale name, the lane name, the house code, and the SAME id
 * the runtime uses for the event. The runtime's event activities carry the
 * status. So a sale is a group of lane records that share a name and a house,
 * a lane is live when the runtime says its event is running, and this joins
 * the two by id. It is the join the portal already makes for its dealers.
 *
 * A sale's `auctionHouseCode` is the slug of an administration server Auction
 * House, which is the code the house switcher hands in.
 *
 * Pure, so a test can hold it without a store or a runtime.
 */

/** The runtime statuses under which a clerk can act on a lane. */
export const LIVE_STATUSES = Object.freeze(['STARTED', 'RESUMED', 'PAUSED'])

/** The runtime event types a clerk runs; an automated or timed lane runs itself. */
export const CLERKABLE_EVENT_TYPES = Object.freeze(['simulcastAuction'])

// The runtime's words for the format, and the desktop library's. The card
// keeps the library's value; what a person reads is the card's business.
const SALE_TYPES = Object.freeze({
    automatedAuction: 'automated',
    simulcastAuction: 'simulcast',
    timedAuction: 'timed'
})

export function saleTypeOf(eventType) {
    return SALE_TYPES[eventType] ?? 'simulcast'
}

/**
 * The runtime's list endpoints answer either a bare array or an envelope.
 * Whichever it is, the rows are what matter.
 */
export function unwrapList(answer) {
    if (Array.isArray(answer)) {
        return answer
    }
    if (Array.isArray(answer?.retrieved)) {
        return answer.retrieved
    }
    if (Array.isArray(answer?.items)) {
        return answer.items
    }
    return []
}

/**
 * @param {Object} input
 * @param {Array}  [input.sales]      vibe-server's sale records (one lane each).
 * @param {Array}  [input.activities] The runtime's event-activity records (`{ id, status }`).
 * @param {string} [input.houseCode]  Keep only this house's sales; empty keeps every house.
 * @returns {Array} `[{ id, name, houseCode, location, lanes: [{ eventId, laneName, saleType, clerkable, status, runCount, location }] }]`,
 *                  sales with no running lane left out.
 */
export function groupLiveSales({ sales = [], activities = [], houseCode = '' } = {}) {
    const statusById = new Map(activities.map((activity) => [activity.id, activity.status]))
    const groups = new Map()
    for (const sale of sales) {
        if (houseCode && sale.auctionHouseCode !== houseCode) {
            continue
        }
        const eventId = idOf(sale)
        const status = statusById.get(eventId) ?? null
        if (!LIVE_STATUSES.includes(status)) {
            continue
        }
        const key = `${sale.auctionHouseCode ?? ''}::${sale.saleName}`
        if (!groups.has(key)) {
            groups.set(key, {
                id: key,
                name: sale.saleName,
                houseCode: sale.auctionHouseCode ?? null,
                location: sale.location ?? null,
                lanes: []
            })
        }
        groups.get(key).lanes.push({
            eventId,
            laneName: sale.laneName ?? 'Lane',
            saleType: saleTypeOf(sale.eventType),
            clerkable: CLERKABLE_EVENT_TYPES.includes(sale.eventType),
            status,
            runCount: Array.isArray(sale.runList) ? sale.runList.length : 0,
            location: sale.location ?? null
        })
    }
    return [...groups.values()]
}
